import React from 'react';
import {MergeType} from '@mui/icons-material';
import {Button, Typography} from '@mui/material';
import {FormContainer, SubmitHandler} from 'react-hook-form-mui';
import {toast} from 'react-toastify';
import ActionMenuItemConfirmDialog from 'components/list/actionMenu/ActionMenuItemConfirmDialog';
import {ActionMenuItemProps} from 'components/list/actionMenu/ActionMenuItem';
import {useRecordContext} from 'components/record/useRecordContext';
import {useResourceContext} from 'components/record/ResourceContext';
import {useListContext} from 'components/list/ListContext';
import {useDialogContext} from 'components/dialog/DialogContext';
import {useMutate} from 'hooks/useFetchData';
import {AutocompleteInput} from 'components/forms/Inputs';

const MERGE_FORM_ID = 'merge_control';

type MergeControlFormValues = {
  targetId: number;
};

type ControlOption = {
  id: number;
  label: string;
};

const MergeControlForm = ({onSuccess}: {onSuccess?: () => void}) => {
  const {id, record} = useRecordContext();
  const {params} = useResourceContext();
  const {list} = useListContext();
  const {close} = useDialogContext();
  const [mutate] = useMutate<{item: {id: number; name?: string}}>('merge', 'Control', {
    variables: params,
    refetchQueries: ['GetControls']
  });

  const options = React.useMemo<ControlOption[]>(
    () =>
      list
        .filter((control) => control.id !== id)
        .map((control) => ({
          id: Number(control.id),
          label: typeof control.name === 'string' ? control.name : String(control.id)
        })),
    [list, id]
  );

  const handleMerge: SubmitHandler<MergeControlFormValues> = (payload) => {
    const targetId = Number(payload.targetId);
    if (!targetId || targetId === Number(id)) {
      toast.error('Select a different control to merge into');
      return;
    }
    if (typeof mutate !== 'function') {
      return;
    }
    mutate({variables: {...params, id, targetId}})
      .then((res) => {
        if (res.data?.item) {
          const targetName = res.data.item.name || res.data.item.id;
          toast.success(`${record.name || 'Control'} has been merged into ${targetName}`);
          if (typeof onSuccess === 'function') {
            onSuccess();
          }
          close();
        }
      })
      .catch((error: {message?: string}) => {
        if (error.message) {
          toast.error(error.message);
        }
      });
  };

  return (
    <FormContainer FormProps={{id: MERGE_FORM_ID, noValidate: true}} onSuccess={handleMerge}>
      <Typography variant="body2" sx={{mb: 1}}>
        Delete <strong>{record.name || `control #${id}`}</strong> and replace it with the selected control on all courses.
      </Typography>
      <AutocompleteInput<ControlOption> required fullWidth name="targetId" label="Merge to" options={options} />
    </FormContainer>
  );
};

const ActionMenuItemMergeControl = (props: ActionMenuItemProps) => {
  const {onSuccess, ...rest} = props;
  const {id, record} = useRecordContext();

  return (
    <ActionMenuItemConfirmDialog
      {...rest}
      label="Merge to"
      icon={<MergeType />}
      confirmTitle={`Merge ${record.name || `control #${id}`} to`}
      confirmContent={<MergeControlForm onSuccess={onSuccess} />}
      primaryButton={
        <Button type="submit" form={MERGE_FORM_ID} variant="contained">
          Merge
        </Button>
      }
    />
  );
};

export default ActionMenuItemMergeControl;
